import React, { useCallback, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@material-ui/core';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import DeleteOutlineRoundedIcon from '@material-ui/icons/DeleteOutlineRounded';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import UsersAPI from '../../user-api';
import Button from '../../../../components/common/Button';
import { UserContext } from '../userContext';

const DEFAULT_FORM = {
  reason: 'manual',
  blockedFrom: '',
  blockedUntil: '',
};

const DEFAULT_FUNCTIONALITY = 'schedule_mentorship';

const REASON_LABELS = {
  confirmed_but_did_not_attend: 'Confirmó y no asistió',
  no_show: 'Confirmó y no asistió',
  manual: 'Manual',
};

const REASON_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  {
    value: 'confirmed_but_did_not_attend',
    label: 'Confirmó y no asistió',
  },
];

const formatDateTime = (value) =>
  value ? moment(value).format('DD/MM/YYYY HH:mm') : '-';

const toDateTimeInput = (value) =>
  value ? moment(value).format('YYYY-MM-DDTHH:mm') : '';

const getUserDisplayName = (user) =>
  user?.name ||
  `${user?.firstName || ''} ${user?.lastNames || user?.lastName || ''}`.trim() ||
  user?.email ||
  'este usuario';

const Restrictions = ({ setShowAlert, setMessageAlert }) => {
  const [state] = useContext(UserContext);
  const { user } = state;
  const [restrictions, setRestrictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreatingRestriction, setIsCreatingRestriction] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editing, setEditing] = useState({});
  const [restrictionToDelete, setRestrictionToDelete] = useState(null);
  const [deletingRestrictionId, setDeletingRestrictionId] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const isDeletingRestriction = deletingRestrictionId !== null;

  const showMessage = useCallback(
    (message, type = 'success') => {
      setMessageAlert({ message, type });
      setShowAlert(true);
    },
    [setMessageAlert, setShowAlert]
  );

  const loadRestrictions = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const response = await UsersAPI.getUserRestrictions(user.id);
      setRestrictions(response.data?.data || []);
    } catch (error) {
      showMessage(
        error?.data?.message || 'No fue posible cargar las restricciones.',
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [showMessage, user?.id]);

  useEffect(() => {
    loadRestrictions();
  }, [loadRestrictions]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const setFormField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();

    if (isCreatingRestriction) {
      return;
    }

    try {
      setIsCreatingRestriction(true);
      await UsersAPI.createUserRestriction({
        userId: user.id,
        functionality: DEFAULT_FUNCTIONALITY,
        ...form,
      });
      setForm(DEFAULT_FORM);
      await loadRestrictions();
      showMessage('Restricción creada correctamente.');
    } catch (error) {
      showMessage(
        error?.data?.message || 'No fue posible crear la restricción.',
        'error'
      );
    } finally {
      setIsCreatingRestriction(false);
    }
  };

  const startEditing = (restriction) => {
    setEditingId(restriction.id);
    setEditing({
      reason: restriction.reason,
      blockedFrom: toDateTimeInput(restriction.blockedFrom),
      blockedUntil: toDateTimeInput(restriction.blockedUntil),
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditing({});
  };

  const handleEditingChange = (event) => {
    const { name, value } = event.target;
    setEditing((current) => ({ ...current, [name]: value }));
  };

  const setEditingField = (name, value) => {
    setEditing((current) => ({ ...current, [name]: value }));
  };

  const saveEditing = async (restrictionId) => {
    try {
      await UsersAPI.updateUserRestriction(restrictionId, editing);
      cancelEditing();
      await loadRestrictions();
      showMessage('Restricción actualizada correctamente.');
    } catch (error) {
      showMessage(
        error?.data?.message || 'No fue posible actualizar la restricción.',
        'error'
      );
    }
  };

  const openDeleteDialog = (restriction) => {
    if (isDeletingRestriction) {
      return;
    }

    setDeleteError('');
    setRestrictionToDelete(restriction);
  };

  const closeDeleteDialog = () => {
    if (isDeletingRestriction) {
      return;
    }

    setDeleteError('');
    setRestrictionToDelete(null);
  };

  const deleteRestriction = async () => {
    if (!restrictionToDelete?.id || isDeletingRestriction) {
      return;
    }

    const restrictionId = restrictionToDelete.id;

    try {
      setDeleteError('');
      setDeletingRestrictionId(restrictionId);
      await UsersAPI.deleteUserRestriction(restrictionId);
      setRestrictions((current) =>
        current.filter(
          (restriction) => String(restriction.id) !== String(restrictionId)
        )
      );
      if (String(editingId) === String(restrictionId)) {
        cancelEditing();
      }
      setRestrictionToDelete(null);
      showMessage('Restricción eliminada correctamente.');
    } catch (error) {
      const errorMessage =
        error?.data?.detail ||
        error?.data?.message ||
        error?.message ||
        (typeof error === 'string' ? error : '') ||
        'No fue posible eliminar la restricción.';
      setDeleteError(errorMessage);
    } finally {
      setDeletingRestrictionId(null);
    }
  };

  return (
    <div className="edit-information-user user-restrictions">
      <div className="user-restrictions__header">
        <div>
          <span className="user-restrictions__eyebrow">Control de acceso</span>
          <h3>Restricciones del usuario</h3>
        </div>
        <span className="user-restrictions__count">
          {restrictions.length} registradas
        </span>
      </div>

      <form
        className="personal-info user-restrictions__panel"
        onSubmit={handleCreate}
      >
        <div className="user-restrictions__section-heading">
          <h4>Crear restricción manual</h4>
          <p>Bloquea temporalmente el agendamiento para este usuario.</p>
        </div>
        <div className="user-restrictions__create-row">
          <div className="user-restrictions__field user-restrictions__field--reason">
            <span>Motivo</span>
            <div className="user-restrictions__segmented">
              {REASON_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`user-restrictions__segment ${
                    form.reason === option.value ? 'is-active' : ''
                  }`}
                  onClick={() => setFormField('reason', option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <label className="user-restrictions__field user-restrictions__field--date">
            <span>Bloqueado desde</span>
            <input
              className="user-restrictions__control"
              type="datetime-local"
              name="blockedFrom"
              value={form.blockedFrom}
              onChange={handleChange}
            />
          </label>
          <label className="user-restrictions__field user-restrictions__field--date">
            <span>Bloqueado hasta</span>
            <input
              className="user-restrictions__control"
              type="datetime-local"
              name="blockedUntil"
              value={form.blockedUntil}
              onChange={handleChange}
              required
            />
          </label>
          <div className="user-restrictions__actions">
            <Button
              type="submit"
              widthFull
              loading={isCreatingRestriction}
              disabled={isCreatingRestriction}
              style={
                isCreatingRestriction ? { pointerEvents: 'none' } : undefined
              }
            >
              Crear restricción
            </Button>
          </div>
        </div>
      </form>

      <div className="personal-info user-restrictions__panel user-restrictions__panel--list">
        <div className="user-restrictions__section-heading">
          <h4>Restricciones registradas</h4>
          <p>Administra bloqueos activos o históricos del usuario.</p>
        </div>
        {loading ? (
          <p className="user-restrictions__empty">Cargando restricciones...</p>
        ) : restrictions.length ? (
          <div className="user-restrictions__table-wrapper">
            <table className="user-restrictions__table">
              <thead>
                <tr>
                  <th>Motivo</th>
                  <th>Desde</th>
                  <th>Hasta</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {restrictions.map((restriction) => {
                  const isEditing = editingId === restriction.id;
                  return (
                    <tr key={restriction.id}>
                      <td>
                        {isEditing ? (
                          <div className="user-restrictions__segmented user-restrictions__segmented--compact">
                            {REASON_OPTIONS.map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                className={`user-restrictions__segment ${
                                  editing.reason === option.value
                                    ? 'is-active'
                                    : ''
                                }`}
                                onClick={() =>
                                  setEditingField('reason', option.value)
                                }
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        ) : (
                          REASON_LABELS[restriction.reason] ||
                          restriction.reason
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            className="user-restrictions__table-control"
                            type="datetime-local"
                            name="blockedFrom"
                            value={editing.blockedFrom}
                            onChange={handleEditingChange}
                          />
                        ) : (
                          formatDateTime(restriction.blockedFrom)
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            className="user-restrictions__table-control"
                            type="datetime-local"
                            name="blockedUntil"
                            value={editing.blockedUntil}
                            onChange={handleEditingChange}
                          />
                        ) : (
                          formatDateTime(restriction.blockedUntil)
                        )}
                      </td>
                      <td>
                        <span
                          className={`user-restrictions__status ${
                            restriction.active
                              ? 'user-restrictions__status--active'
                              : 'user-restrictions__status--expired'
                          }`}
                        >
                          {restriction.active ? 'Activa' : 'Expirada'}
                        </span>
                      </td>
                      <td>
                        <div className="user-restrictions__row-actions">
                          {isEditing ? (
                            <>
                              <button
                                className="user-restrictions__action-button user-restrictions__action-button--primary"
                                type="button"
                                onClick={() => saveEditing(restriction.id)}
                              >
                                Guardar
                              </button>
                              <button
                                className="user-restrictions__action-button"
                                type="button"
                                onClick={cancelEditing}
                              >
                                Cancelar
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="user-restrictions__action-button"
                                type="button"
                                onClick={() => startEditing(restriction)}
                              >
                                Editar
                              </button>
                              <button
                                className="user-restrictions__action-button user-restrictions__action-button--danger"
                                type="button"
                                onClick={() => openDeleteDialog(restriction)}
                              >
                                Eliminar
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="user-restrictions__empty">
            Este usuario no tiene restricciones registradas.
          </div>
        )}
      </div>

      <Dialog
        open={Boolean(restrictionToDelete)}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-restriction-title"
        aria-describedby="delete-restriction-description"
        maxWidth="xs"
        fullWidth
        disableBackdropClick={isDeletingRestriction}
        disableEscapeKeyDown={isDeletingRestriction}
        PaperProps={{
          className: 'user-restrictions__delete-dialog',
        }}
        BackdropProps={{
          className: 'user-restrictions__delete-backdrop',
        }}
      >
        <DialogTitle
          disableTypography
          className="user-restrictions__delete-dialog-title"
        >
          <span className="user-restrictions__delete-dialog-icon">
            <DeleteOutlineRoundedIcon />
          </span>
          <span className="user-restrictions__delete-dialog-heading">
            <span>Confirmar eliminación</span>
            <h2 id="delete-restriction-title">Eliminar restricción</h2>
          </span>
          <IconButton
            className="user-restrictions__delete-dialog-close"
            type="button"
            aria-label="Cerrar"
            onClick={closeDeleteDialog}
            disabled={isDeletingRestriction}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className="user-restrictions__delete-dialog-content">
          <p
            id="delete-restriction-description"
            className="user-restrictions__delete-dialog-copy"
          >
            Estás a punto de eliminar esta restricción de{' '}
            <strong>{getUserDisplayName(user)}</strong>.
          </p>

          {restrictionToDelete ? (
            <div className="user-restrictions__delete-summary">
              <div className="user-restrictions__delete-summary-item user-restrictions__delete-summary-item--wide">
                <span>Motivo</span>
                <strong>
                  {REASON_LABELS[restrictionToDelete.reason] ||
                    restrictionToDelete.reason}
                </strong>
              </div>
              <div className="user-restrictions__delete-summary-item">
                <span>Desde</span>
                <strong>
                  {formatDateTime(restrictionToDelete.blockedFrom)}
                </strong>
              </div>
              <div className="user-restrictions__delete-summary-item">
                <span>Hasta</span>
                <strong>
                  {formatDateTime(restrictionToDelete.blockedUntil)}
                </strong>
              </div>
              <div className="user-restrictions__delete-summary-item user-restrictions__delete-summary-item--wide">
                <span>Estado actual</span>
                <strong
                  className={
                    restrictionToDelete.active
                      ? 'user-restrictions__delete-state user-restrictions__delete-state--active'
                      : 'user-restrictions__delete-state'
                  }
                >
                  {restrictionToDelete.active ? 'Activa' : 'Expirada'}
                </strong>
              </div>
            </div>
          ) : null}

          <div className="user-restrictions__delete-dialog-warning">
            <WarningRoundedIcon />
            <p>
              <strong>Esta acción no se puede deshacer.</strong>
              Si es el único bloqueo activo, el usuario recuperará el acceso al
              agendamiento.
            </p>
          </div>

          {deleteError ? (
            <div
              className="user-restrictions__delete-dialog-error"
              role="alert"
            >
              {deleteError}
            </div>
          ) : null}
        </DialogContent>

        <DialogActions className="user-restrictions__delete-dialog-actions">
          <button
            className="user-restrictions__delete-dialog-secondary"
            type="button"
            onClick={closeDeleteDialog}
            disabled={isDeletingRestriction}
            autoFocus
          >
            Conservar restricción
          </button>
          <button
            className="user-restrictions__delete-dialog-danger"
            type="button"
            onClick={deleteRestriction}
            disabled={isDeletingRestriction}
            aria-busy={isDeletingRestriction}
          >
            {isDeletingRestriction ? (
              <>
                <span
                  className="user-restrictions__delete-dialog-spinner"
                  aria-hidden="true"
                />
                Eliminando...
              </>
            ) : (
              <>
                <DeleteOutlineRoundedIcon />
                Eliminar restricción
              </>
            )}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Restrictions;
